import { useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { api } from '../api';
import type { CustomNodeWhatsNewEntry } from '../types';
import '../styles/custom-nodes.css';

export function CustomNodesWhatsNewPage() {
  const [entries, setEntries] = useState<CustomNodeWhatsNewEntry[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    let active = true;
    setLoading(true);
    api
      .customNodesWhatsNew()
      .then((result) => {
        if (active) setEntries(Array.isArray(result) ? result : []);
      })
      .catch((err) => {
        if (active) setError((err as Error).message);
      })
      .finally(() => {
        if (active) setLoading(false);
      });
    return () => {
      active = false;
    };
  }, []);

  // Group entries (already date-desc from the API) into date buckets.
  const groups = useMemo(() => {
    const out: Array<{ date: string; items: CustomNodeWhatsNewEntry[] }> = [];
    for (const entry of entries) {
      const date = entry.date || 'Undated';
      const last = out[out.length - 1];
      if (last && last.date === date) {
        last.items.push(entry);
      } else {
        out.push({ date, items: [entry] });
      }
    }
    return out;
  }, [entries]);

  return (
    <div className="custom-node-detail">
      <div className="custom-node-detail__breadcrumb">
        <Link to="/custom-nodes">
          <span className="material-icons">arrow_back</span>
          Custom Nodes
        </Link>
      </div>

      <h1>What&apos;s New</h1>
      <p className="hint">Recent versions and changes across all custom nodes you can access.</p>

      {error ? <div className="alert alert-error">{error}</div> : null}
      {loading ? <div className="loading">Loading…</div> : null}

      {!loading && entries.length === 0 ? (
        <div className="card card-subtle custom-nodes-empty">
          <span className="material-icons">history</span>
          <p>No changelog entries yet.</p>
        </div>
      ) : null}

      {groups.map((group) => (
        <section className="card cn-whatsnew-group" key={group.date}>
          <h2 className="cn-whatsnew-date">{group.date}</h2>
          <ul className="cn-whatsnew-list">
            {group.items.map((item, index) => (
              <li key={`${item.nodeId}-${item.version}-${index}`} className="cn-whatsnew-item">
                <div className="cn-whatsnew-icon">
                  {item.iconDataUrl ? (
                    <img src={item.iconDataUrl} alt="" />
                  ) : (
                    <span className="material-icons">extension</span>
                  )}
                </div>
                <div className="cn-whatsnew-body">
                  <div className="cn-whatsnew-head">
                    <Link to={`/custom-nodes/${item.nodeId}`} className="cn-whatsnew-name">
                      {item.name}
                    </Link>
                    <span className="cn-whatsnew-ver">v{item.version}</span>
                  </div>
                  {item.notes ? <p className="cn-whatsnew-notes">{item.notes}</p> : null}
                </div>
              </li>
            ))}
          </ul>
        </section>
      ))}
    </div>
  );
}
